import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { tv } from 'tailwind-variants';

import { cls } from '@/utils/cls';

const style = tv({
  slots: {
    container: `
      flex
      size-full
      items-center
      justify-center
      bg-gray-100
    `,
    icon: `
      size-20
      text-red-500
    `,
    title: `
      text-4xl
      font-bold
      tracking-tight
      text-gray-900
    `,
    description: `
      text-gray-500
    `,
  },
});

export type ErrorFallbackProps = {
  className?: string;
  eventId?: string;
  resetError(): void;
};

export function ErrorFallback({
  className,
  resetError,
  eventId,
}: ErrorFallbackProps) {
  const history = useHistory();
  const { container, icon, title, description } = style();

  useEffect(
    function () {
      const unlisten = history.listen(function () {
        resetError();
      });

      return () => unlisten();
    },
    [history, resetError],
  );

  return (
    <div className={container({ className })}>
      <div className="flex flex-col items-center space-y-6">
        <XMarkIcon className={icon()} />
        <div className="max-w-prose space-y-4 text-center">
          <h1 className={title()}>Oops, something went wrong</h1>
          <p className={description()}>
            We're sorry, but an unexpected error has occurred. Please try again
            later.
          </p>
        </div>
        {eventId ? <pre className="text-sm">Event ID: {eventId}</pre> : null}
      </div>
    </div>
  );
}

export function FullPageErrorFallback({
  className,
  ...props
}: ErrorFallbackProps) {
  return (
    <ErrorFallback {...props} className={cls('h-screen w-screen', className)} />
  );
}
